import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    Divider
} from "@mui/material";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    Timestamp
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import Menu from "../../panel/Menu";

function Datewise() {
    const printRef = useRef();

    const [propertyId, setPropertyId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [bookingData, setBookingData] = useState([]);

    /* ================= Toast ================= */
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000
    });

    /* ================= AUTH ================= */
    useEffect(() => {
        auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            }
        });
    }, []);

    /* ================= GET REPORT ================= */
    const getReport = async () => {
        if (!fromDate || !toDate || !propertyId) {
            Toast.fire({ icon: "error", title: "Select dates" });
            return;
        }

        setBookingData([]);

        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(toDate);
        end.setDate(end.getDate() + 1);

        /* === PAYMENTS === */
        const paySnap = await getDocs(
            query(
                collection(db, "payhistory"),
                where("propertyId", "==", propertyId),
                where("status", "==", "Working"),
                where("createdAt", ">=", Timestamp.fromDate(start)),
                where("createdAt", "<", Timestamp.fromDate(end))
            )
        );

        const paymentMap = {};
        paySnap.forEach(d => {
            const data = d.data();
            if (!paymentMap[data.bookingid]) paymentMap[data.bookingid] = [];
            paymentMap[data.bookingid].push(data);
        });

        /* === CHECKINS === */
        const checkinSnap = await getDocs(
            query(
                collection(db, "checkindetails"),
                where("propertyId", "==", propertyId),
                where("status", "in", ["Booked", "Checkout"]),
                where("checkindate", ">=", fromDate),
                where("checkindate", "<=", toDate)
            )
        );

        const checkins = checkinSnap.docs.map(d => d.data());
        const bookingIds = [...new Set(checkins.map(c => c.bookingid))];

        if (!bookingIds.length) {
            Toast.fire({ icon: "info", title: "No records" });
            return;
        }

        /* === RESERVATIONS === */
        const bookingMap = {};
        while (bookingIds.length) {
            const chunk = bookingIds.splice(0, 30);
            const snap = await getDocs(
                query(collection(db, "reservations"), where("__name__", "in", chunk))
            );
            snap.forEach(d => (bookingMap[d.id] = d.data()));
        }

        /* === FINAL === */
        const final = [];

        for (const row of checkins) {
            const booking = bookingMap[row.bookingid];
            if (!booking) continue;

            const payments = paymentMap[row.bookingid] || [];
            const paid = payments.reduce(
                (s, p) => s + (p.amount1 || 0) + (p.amount2 || 0),
                0
            );

            const room = Number(booking.rooamount || 0);
            const service =
                Number(booking.serviceamount || 0) /
                Number(booking.noofdays || 1);

            const total = room + service;

            final.push({
                date: row.checkindate,
                customer: booking.customers?.custname,
                username: row.username || "Admin",
                room,
                service,
                total,
                paid,
                pending: total - paid
            });
        }

        setBookingData(final);
    };

    /* ================= GROUP BY DATE ================= */
    const grouped = bookingData.reduce((acc, r) => {
        if (!acc[r.date]) acc[r.date] = [];
        acc[r.date].push(r);
        return acc;
    }, {});

    /* ================= PRINT ================= */
 const handlePrint = () => {
        window.print();
    };
    return (
        <>
            <Menu />

            {/* PAGE WRAPPER */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    overflowX: "hidden",
                }}
            >
                <Box className="content-wrapper"
                    sx={{
                        width: "100%",
                        px: 2,
                        p: 3
                    }}
                >
                    <Typography variant="h6" gutterBottom align="left">
                        Datewise Booking Report
                    </Typography>

                    {/* FILTER */}
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        type="date"
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        type="date"
                                        label="To Date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    sx={{ display: "flex", alignItems: "center" }}
                                >
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={getReport}
                                    >
                                        Get Report
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* REPORT */}
                    <Card className="print-area" ref={printRef}>
                        <CardContent>
                            {Object.entries(grouped).map(([date, rows]) => {
                                const subtotal = rows.reduce((s, r) => s + r.total, 0);

                                return (
                                    <Box key={date} mb={4}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight="bold"
                                            sx={{ mb: 1 }}
                                        >
                                            Date : {moment(date).format("DD-MM-YYYY")}
                                        </Typography>

                                        <TableContainer
                                            component={Paper}
                                            sx={{
                                                overflowX: "hidden"   // ✅ NO SCROLLBAR
                                            }}
                                        >
                                            <Table
                                                size="small"
                                                sx={{
                                                    tableLayout: "fixed", // ✅ IMPORTANT
                                                    width: "100%"
                                                }}
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: "8%" }}>Sr No</TableCell>
                                                        <TableCell sx={{ width: "42%" }}>Customer</TableCell>
                                                        <TableCell align="right" sx={{ width: "15%" }}>
                                                            Room
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: "15%" }}>
                                                            Service
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ width: "20%" }}>
                                                            Total
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {rows.map((r, i) => (
                                                        <TableRow key={i}>
                                                            <TableCell>{i + 1}</TableCell> {/* ✅ SR NO */}

                                                            <TableCell
                                                                sx={{
                                                                    wordBreak: "break-word",
                                                                    whiteSpace: "normal"
                                                                }}
                                                            >
                                                                {r.customer}
                                                            </TableCell>

                                                            <TableCell align="right">{r.room}</TableCell>
                                                            <TableCell align="right">{r.service}</TableCell>
                                                            <TableCell align="right">{r.total}</TableCell>
                                                        </TableRow>
                                                    ))}

                                                    {/* DATE TOTAL */}
                                                    <TableRow sx={{ backgroundColor: "#f3f6f9" }}>
                                                        <TableCell colSpan={4} align="right" fontWeight="bold">
                                                            Date Total
                                                        </TableCell>
                                                        <TableCell align="right" fontWeight="bold">
                                                            {subtotal}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>

                                            </Table>
                                        </TableContainer>

                                        <Divider sx={{ mt: 3 }} />
                                    </Box>
                                );
                            })}
                        </CardContent>
                    </Card>

                    {/* PRINT */}
                    {/* <Box textAlign="center" mt={3}>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handlePrint}
                        >
                            Print Report
                        </Button>
                    </Box> */}
                </Box>
            </Box>
        </>

    );
}

export default Datewise;
